import { useMemo, useRef, useEffect } from 'react';
import { useWatch, type Control, type UseFormSetValue, type UseFormGetValues, type UseFormUnregister, type UseFormClearErrors } from 'react-hook-form';
import type { FormDescriptor, FormFieldDescriptor } from '@finance/contracts';
import { resolveFieldState, type ResolvedFieldState } from './field-state';
import { evaluateCondition } from '../conditions/evaluate-condition';
import { resolveComputedValues, getComputedFields } from './computed-values';
import type { EvaluationContext } from '../conditions/evaluate-condition';

function buildFieldIdToNameMap(descriptor: FormDescriptor): Map<string, string> {
  const map = new Map<string, string>();
  for (const field of descriptor.fields) {
    map.set(field.id as string, field.name);
  }
  return map;
}

function buildNameToIdMap(descriptor: FormDescriptor): Map<string, string> {
  const map = new Map<string, string>();
  for (const field of descriptor.fields) {
    map.set(field.name, field.id as string);
  }
  return map;
}

function collectConditionFieldsDeep(cond: unknown, acc: Set<string>): void {
  if (!cond || typeof cond !== 'object') return;
  const c = cond as Record<string, unknown>;
  if ('field' in c && typeof c.field === 'string') {
    acc.add(c.field);
  }
  for (const value of Object.values(c)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        collectConditionFieldsDeep(item, acc);
      }
      continue;
    }
    if (value && typeof value === 'object') {
      collectConditionFieldsDeep(value, acc);
    }
  }
}

function buildDependencyIndex(descriptor: FormDescriptor, idToName: Map<string, string>): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();

  const addDeps = (cond: unknown, targetField: string) => {
    const deps = new Set<string>();
    collectConditionFieldsDeep(cond, deps);
    for (const dep of deps) {
      const resolvedField = idToName.get(dep) ?? dep;
      if (resolvedField !== targetField) {
        if (!index.has(resolvedField)) index.set(resolvedField, new Set());
        index.get(resolvedField)!.add(targetField);
      }
    }
  };

  for (const field of descriptor.fields) {
    const state = field.state;
    if (!state) continue;

    addDeps(state.visibilityCondition, field.name);
    addDeps(state.readonlyCondition, field.name);
    addDeps(state.requiredCondition, field.name);
    addDeps(state.disabledCondition, field.name);
  }

  for (const action of descriptor.actions) {
    if (action.visibilityCondition) {
      addDeps(action.visibilityCondition, `__action_${action.id}`);
    }
  }

  return index;
}

export interface UseFormFieldStateOptions {
  contract: FormDescriptor;
  control: Control<Record<string, unknown>>;
  getValues: UseFormGetValues<Record<string, unknown>>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  unregister: UseFormUnregister<Record<string, unknown>>;
  clearErrors: UseFormClearErrors<Record<string, unknown>>;
  evalCtx: EvaluationContext;
  pending?: boolean;
}

export interface UseFormFieldStateResult {
  formValuesWithIds: Record<string, unknown>;
  fieldStates: Map<string, ResolvedFieldState>;
  visibleFields: FormFieldDescriptor[];
  resolvedSections: Map<string, boolean>;
}

export function useFormFieldState({
  contract,
  control,
  getValues,
  setValue,
  unregister,
  clearErrors,
  evalCtx,
  pending = false,
}: UseFormFieldStateOptions): UseFormFieldStateResult {
  const idToName = useMemo(() => buildFieldIdToNameMap(contract), [contract]);
  const nameToId = useMemo(() => buildNameToIdMap(contract), [contract]);
  const dependencyIndex = useMemo(() => buildDependencyIndex(contract, idToName), [contract, idToName]);

  const watchedFields = useMemo(() => [...dependencyIndex.keys()], [dependencyIndex]);
  useWatch({ control, name: watchedFields as never });
  const allValues = useWatch({ control }) as Record<string, unknown>;

  const formValuesWithIds = useMemo(() => {
    const values: Record<string, unknown> = { ...allValues };
    for (const [name, value] of Object.entries(allValues)) {
      const id = nameToId.get(name);
      if (id) values[id] = value;
    }
    return values;
  }, [allValues, nameToId]);

  const previousFieldStatesRef = useRef<Map<string, Pick<ResolvedFieldState, 'visible' | 'readonly' | 'disabled'>>>(new Map());

  const fieldStates = useMemo(() => {
    const map = new Map<string, ResolvedFieldState>();
    const evalValues: Record<string, unknown> = { ...formValuesWithIds };
    for (const field of contract.fields) {
      evalValues[field.id as string] = formValuesWithIds[field.name];
    }
    for (const field of contract.fields) {
      const state = resolveFieldState(field, contract.mode, evalValues, evalCtx, { pending });
      map.set(field.name, state);
      map.set(field.id as string, state);
    }
    return map;
  }, [contract.fields, contract.mode, formValuesWithIds, evalCtx, pending]);

  const visibleFields = useMemo(
    () => contract.fields.filter((f) => fieldStates.get(f.name)?.visible ?? true),
    [contract.fields, fieldStates],
  );

  const resolvedSections = useMemo(() => {
    const map = new Map<string, boolean>();
    const data = { ...formValuesWithIds };
    for (const field of contract.fields) {
      data[field.id as string] = formValuesWithIds[field.name];
    }
    for (const section of contract.sections ?? []) {
      const visible = section.visibilityCondition
        ? evaluateCondition(section.visibilityCondition, { ...evalCtx, data })
        : true;
      map.set(section.id as string, visible);
    }
    return map;
  }, [contract.sections, contract.fields, formValuesWithIds, evalCtx]);

  const computedFieldNames = useMemo(
    () => new Set(getComputedFields(contract.fields).map((f) => f.name)),
    [contract.fields],
  );

  useEffect(() => {
    if (computedFieldNames.size === 0) return;
    const currentValues = getValues();
    const resolved = resolveComputedValues(currentValues, contract.fields);
    for (const field of contract.fields) {
      if (computedFieldNames.has(field.name)) {
        const newVal = resolved[field.name];
        if (newVal !== undefined && newVal !== currentValues[field.name]) {
          setValue(field.name, newVal, { shouldDirty: false, shouldValidate: false });
        }
      }
    }
  }, [formValuesWithIds, contract.fields, computedFieldNames, getValues, setValue]);

  useEffect(() => {
    for (const field of contract.fields) {
      const state = fieldStates.get(field.name);
      if (!state) continue;
      const previousState = previousFieldStatesRef.current.get(field.name);
      const becameHidden = previousState?.visible !== false && !state.visible;
      const becameReadonly = previousState?.readonly !== true && state.readonly;
      const becameDisabled = previousState?.disabled !== true && state.disabled;

      if (becameHidden) {
        const policy = field.value?.hiddenValuePolicy ?? 'unregister';
        if (policy === 'unregister') {
          unregister(field.name, {
            keepValue: false,
            keepError: false,
            keepDirty: false,
            keepTouched: false,
            keepIsValid: false,
          });
        } else if (policy === 'clear') {
          const emptyValue =
            field.fieldType === 'multiselect'
              ? []
              : field.fieldType === 'checkbox' || field.fieldType === 'switch'
                ? false
                : '';
          setValue(field.name, emptyValue, { shouldDirty: true, shouldValidate: false });
          clearErrors(field.name);
        }
      }

      if (becameHidden || becameReadonly || becameDisabled) {
        clearErrors(field.name);
      }

      previousFieldStatesRef.current.set(field.name, {
        visible: state.visible,
        readonly: state.readonly,
        disabled: state.disabled,
      });
    }
  }, [fieldStates, contract.fields, unregister, setValue, clearErrors]);

  return { formValuesWithIds, fieldStates, visibleFields, resolvedSections };
}
